import { useState } from "react";
import { Plus } from "lucide-react";
import {
  Accordion,
  Button,
  EmptyState,
  FormModal,
  Input,
  Table,
  Text,
  type Column,
} from "@marekl/adminer/components";
import { useConfirm } from "@marekl/adminer/hooks";
import { notifySuccess } from "@marekl/adminer/api";
import { formatBytes } from "@marekl/adminer/format";
import { CodeMirrorField } from "../../components/CodeMirrorField";
import { useIsAdmin } from "../../auth/useAuth";
import {
  useDeleteTemplatePartial,
  useTemplatePartials,
  useUpsertTemplatePartial,
  type TemplatePartial,
} from "../../lib/queries/templates";

interface PartialModalProps {
  /** `null` = create a new partial. */
  partial: TemplatePartial | null;
  onClose: () => void;
}

function PartialModal({ partial, onClose }: PartialModalProps) {
  const upsert = useUpsertTemplatePartial();
  const [key, setKey] = useState(partial?.key ?? "");
  const [body, setBody] = useState(partial?.html_body ?? "");

  async function save() {
    try {
      await upsert.mutateAsync({ key: key.trim(), html_body: body });
      notifySuccess("The partial was saved. The change takes effect for future sends.");
      onClose();
    } catch {
      // Shown by the FormModal error slot (the mutation suppresses the toast).
    }
  }

  return (
    <FormModal
      open
      title={partial ? `Partial — ${partial.key}` : "New partial"}
      onClose={onClose}
      width={760}
      busy={upsert.isPending}
      canSubmit={!!key.trim()}
      error={upsert.error ?? undefined}
      onSubmit={() => void save()}
    >
      <Input
        label="Key"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        disabled={!!partial}
        placeholder="set/file.html"
        spellCheck={false}
        hint='Referenced in templates as {{template "set/file.html" .}}.'
      />
      <CodeMirrorField
        label="HTML content"
        value={body}
        onChange={setBody}
        height="360px"
      />
    </FormModal>
  );
}

/**
 * Manager of shared code-template partials (GET/PUT/DELETE
 * /api/template-partials). Rendered only for `kind=code` templates.
 */
export function PartialsSection() {
  const isAdmin = useIsAdmin();
  const partials = useTemplatePartials();
  const remove = useDeleteTemplatePartial();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<TemplatePartial | null | false>(false);

  function confirmDelete(partial: TemplatePartial) {
    void confirm({
      title: "Delete partial",
      description: (
        <>
          Really delete the partial{" "}
          <Text as="span" variant="mono">
            {partial.key}
          </Text>
          ? Templates that include it will stop rendering.
        </>
      ),
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: async () => {
        await remove.mutateAsync({ key: partial.key });
      },
    }).then((confirmed) => {
      if (confirmed) notifySuccess("The partial was deleted.");
    });
  }

  const columns: Column<TemplatePartial>[] = [
    {
      key: "key",
      header: "Key",
      primary: true,
      render: (row) => <Text variant="mono">{row.key}</Text>,
    },
    {
      key: "size",
      header: "Size",
      numeric: true,
      render: (row) => <Text variant="mono">{formatBytes(row.html_body.length)}</Text>,
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <Table.Actions>
          <Table.Action onClick={() => setEditing(row)}>
            {isAdmin ? "Edit" : "View"}
          </Table.Action>
          {isAdmin && (
            <Table.Action variant="danger" onClick={() => confirmDelete(row)}>
              Delete
            </Table.Action>
          )}
        </Table.Actions>
      ),
    },
  ];

  return (
    <>
      {/* Accordion.Item body unmounts while collapsed — safe here: the table
          is pure query-backed display and the editor lives in a modal. Open
          by default to keep the section as discoverable as the Card it was. */}
      <Accordion.Item
        defaultOpen
        title="Partials"
        summary="shared parts for code templates; changes only affect future sends"
        actions={
          isAdmin && (
            <Button size="sm" variant="primary" weight="outline" onClick={() => setEditing(null)}>
              <Plus size={14} aria-hidden="true" />
              Add partial
            </Button>
          )
        }
      >
        <Table<TemplatePartial>
          cards
          columns={columns}
          rows={partials.data ?? []}
          rowKey={(r) => r.key}
          isLoading={partials.isLoading}
          error={partials.error}
          onRetry={partials.refetch}
          empty={
            <EmptyState
              title="No partials"
              description="No shared partial has been created yet."
            />
          }
        />
      </Accordion.Item>

      {editing !== false && (
        <PartialModal partial={editing} onClose={() => setEditing(false)} />
      )}
    </>
  );
}
